<div class="flex flex-col gap-8">
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <div class="flex flex-col">
        <h4 class="text-lg font-bold">{{ 'pages.settings.userManagement.rolesTeamsTab.roles.title' | translate }}</h4>
        <p class="text-xs text-shikenso-purple-base-60">
          {{ 'pages.settings.userManagement.rolesTeamsTab.roles.description' | translate }}
        </p>
      </div>

      <app-create-entity-button
        color="secondary"
        [labelKey]="'pages.settings.userManagement.selectRoleDialog.addCustomRole'"
        [onClick]="onAddCustomRoleClick.bind(this)"
        [permissions]="[EPermissions.roleCreate]"
      />
    </div>

    <div class="roles-teams-datatable-container" data-testid="user-management-roles-datatable">
      <app-datatable
        [rowData]="roleRows() ?? []"
        [columns]="datatableColumns"
        [configurationInput]="datatableConfig"
        [customColumnDef]="[
          { name: 'name', isSortable: false, template: identityColumn },
          { name: 'actions', isSortable: false, template: roleActionsColumn },
        ]"
        [total]="(roleRows() ?? []).length"
        [loaderConfig]="skeletonLoaderConfig"
        [isInitialDataLoaded]="roleRows() !== null"
        [loadingData]="roleRowsLoading()"
        (rowClick)="onRoleTeamRowClick($event)"
      />
    </div>
  </div>

  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <div class="flex flex-col">
        <h4 class="text-lg font-bold">{{ 'pages.settings.userManagement.rolesTeamsTab.teams.title' | translate }}</h4>
        <p class="text-xs text-shikenso-purple-base-60">
          {{ 'pages.settings.userManagement.rolesTeamsTab.teams.description' | translate }}
        </p>
      </div>

      <app-create-entity-button
        color="secondary"
        [labelKey]="'pages.settings.userManagement.selectTeamDialog.addTeam'"
        [onClick]="onAddTeamClick.bind(this)"
        [permissions]="[EPermissions.teamCreate]"
      />
    </div>

    <div class="roles-teams-datatable-container" data-testid="user-management-teams-datatable">
      <app-datatable
        [rowData]="teamRows() ?? []"
        [columns]="datatableColumns"
        [configurationInput]="datatableConfig"
        [customColumnDef]="[
          { name: 'name', isSortable: false, template: identityColumn },
          { name: 'actions', isSortable: false, template: teamActionsColumn },
        ]"
        [total]="(teamRows() ?? []).length"
        [loaderConfig]="skeletonLoaderConfig"
        [isInitialDataLoaded]="teamRows() !== null"
        [loadingData]="teamRowsLoading()"
        (rowClick)="onRoleTeamRowClick($event)"
      />
    </div>
  </div>

  <ng-template #identityColumn let-row="row">
    <div class="flex items-center gap-3 min-w-0">
      <app-icon-badge
        alt=""
        [color]="row.kind === 'team' ? 'var(--shikenso-purple-base-40)' : 'var(--shikenso-purple-base)'"
        [backgroundColor]="row.kind === 'team' ? 'var(--shikenso-base-white)' : 'var(--shikenso-pink-100-40)'"
        [fallbackBackgroundColor]="row.kind === 'team' ? 'var(--shikenso-base-white)' : 'var(--shikenso-pink-100-40)'"
        [content]="row.icon"
        [fallbackText]="row.name"
        [size]="40"
        [iconSize]="24"
        [borderRadius]="100"
        [fontSize]="16"
      />

      <span class="font-bold line-clamp-1 text-ellipsis">{{ row.name }}</span>

      <span class="text-xs text-shikenso-purple-base-60 rounded-full bg-shikenso-base-white px-2 py-1 shrink-0">
        {{
          (row.userCount === 1
            ? 'pages.settings.userManagement.rolesTeamsTab.userCountSingular'
            : 'pages.settings.userManagement.rolesTeamsTab.userCount'
          ) | translate: { count: row.userCount }
        }}
      </span>

      @if (row.avatars.length) {
        <app-icon-badge-list
          class="shrink-0"
          fallbackBackgroundColor="var(--shikenso-base-white)"
          [iconData]="row.avatars"
          [borderColor]="'var(--shikenso-base-white)'"
          [borderWidth]="2"
          [size]="28"
          [iconSize]="28"
          [fontSize]="11"
          [borderRadius]="100"
        />
      }
    </div>
  </ng-template>

  <ng-template #roleActionsColumn let-row="row">
    <div class="flex items-center justify-start">
      <button
        mat-icon-button
        class="mat-secondary-transparent-button-color"
        [matMenuTriggerFor]="menu"
        (click)="$event.stopPropagation()"
      >
        <app-icon src="/icons/three-dots.svg" alt="More" [size]="16" />
      </button>

      <mat-menu #menu="matMenu">
        @if (row.isProtected) {
          <button mat-menu-item (click)="onRoleMenuClick(row)">
            {{ 'pages.settings.userManagement.rolesTeamsTab.menu.viewPermissions' | translate }}
          </button>
        } @else {
          <button
            mat-menu-item
            *hasPermission="[EPermissions.roleReadOwn, EPermissions.roleUpdateOwn]"
            (click)="onRoleMenuClick(row)"
          >
            {{
              (canEditRole(row)
                ? 'pages.settings.userManagement.rolesTeamsTab.menu.editPermissions'
                : 'pages.settings.userManagement.rolesTeamsTab.menu.viewPermissions'
              ) | translate
            }}
          </button>

          @let roleDeleteBlockedMessage = getRoleDeleteBlockedMessage(row);

          <span
            class="block"
            matTooltipPosition="above"
            [matTooltip]="roleDeleteBlockedMessage ?? ''"
            [matTooltipDisabled]="!roleDeleteBlockedMessage"
          >
            <button
              mat-menu-item
              *hasPermission="EPermissions.roleDeleteOwn"
              [disabled]="!!roleDeleteBlockedMessage"
              (click)="onDeleteRoleClick(row)"
            >
              {{ 'shared.form.delete' | translate }}
            </button>
          </span>
        }
      </mat-menu>
    </div>
  </ng-template>

  <ng-template #teamActionsColumn let-row="row">
    <div class="flex items-center justify-start">
      <button
        mat-icon-button
        class="mat-secondary-transparent-button-color"
        [matMenuTriggerFor]="menu"
        (click)="$event.stopPropagation()"
      >
        <app-icon src="/icons/three-dots.svg" alt="More" [size]="16" />
      </button>

      <mat-menu #menu="matMenu">
        <button mat-menu-item *hasPermission="EPermissions.teamUpdateOwn" (click)="onEditTeamClick(row)">
          {{ 'pages.settings.userManagement.rolesTeamsTab.menu.editTeam' | translate }}
        </button>

        @let teamDeleteBlockedMessage = getTeamDeleteBlockedMessage(row);

        <span
          class="block"
          matTooltipPosition="above"
          [matTooltip]="teamDeleteBlockedMessage ?? ''"
          [matTooltipDisabled]="!teamDeleteBlockedMessage"
        >
          <button
            mat-menu-item
            *hasPermission="EPermissions.teamDeleteOwn"
            [disabled]="!!teamDeleteBlockedMessage"
            (click)="onDeleteTeamClick(row)"
          >
            {{ 'shared.form.delete' | translate }}
          </button>
        </span>
      </mat-menu>
    </div>
  </ng-template>
</div>
